import React, {
    useState,
    useMemo,
    useCallback,
    memo,
    useEffect,
  } from 'react';
  import {
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    IconButton,
  } from '@mui/material';
  import { useTranslation } from 'react-i18next';
  import editIconImage from '../../../assests/images/Icons/editIcon.svg';
  import { beautifyNumber } from '../../../Utils/FormatNumber';
  import SummaryRowLabel, { SummaryChildLabel, summaryChildRowSx } from '../../Widgets/SummaryRowLabel/SummaryRowLabel';
  import Constants from '../../../assests/Constants/constants.js';

  /** Match PaymentAcceptanceTable scrollbar look for TableContainer overflow. */
  const TABLE_SCROLLBAR_SX = {
    scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
    scrollbarColor: '#d8daf2 transparent',
    '&::-webkit-scrollbar': {
      width: '6px',
      height: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#d8daf2',
      borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
  };

  // Resolve a numeric foreign-currency code to a display label like 'מט"ח ($)'.
  const buildFcLabel = (currencyCode) => {
    if (currencyCode === null || currencyCode === undefined) return 'מט"ח';
    const name = Object.entries(Constants.ForeignCurrencyCode || {})
      .find(([, value]) => value === currencyCode)?.[0];
    const symbol = name ? (Constants.ForeignCurrencySymbol?.[name] || name) : '';
    return symbol ? `מט"ח (${symbol})` : 'מט"ח';
  };

  const COLS = (t, showFc, fcLabel, fcOnly) => [
    {
      id: 'edit',
      width: 40,
      label: '',
      render: ({ row, onEdit }) => (
        <IconButton size="small" onClick={() => onEdit(row)}>
          <img src={editIconImage} alt="edit" />
        </IconButton>
      ),
    },
    { id: 'creditAccountName', label: t('Credit Account'), type: 'string' },
    { id: 'debitAccountName', label: t('Debit Account'), type: 'string' },
    ...(fcOnly
      ? [
          { id: 'foreignCurrencyDebit', label: `${t('Debit')} ${fcLabel}`, type: 'number' },
          { id: 'foreignCurrencyCredit', label: `${t('Credit')} ${fcLabel}`, type: 'number' },
          { id: 'foreignCurrencyBalance', label: `${t('Balance')} ${fcLabel}`, type: 'number' },
        ]
      : [
          { id: 'debitAccount', label: t('Debit'), type: 'number' },
          { id: 'creditAccount', label: t('Credit'), type: 'number' },
          { id: 'balance', label: t('Balance'), type: 'number' },
          ...(showFc
            ? [
                { id: 'foreignCurrencyDebit', label: `${t('Debit')} ${fcLabel}`, type: 'number' },
                { id: 'foreignCurrencyCredit', label: `${t('Credit')} ${fcLabel}`, type: 'number' },
                { id: 'foreignCurrencyBalance', label: `${t('Balance')} ${fcLabel}`, type: 'number' },
              ]
            : []),
        ]),
    { id: 'uploadDate', label: t('ValueDate'), type: 'date' },
    { id: 'documentDate', label: t('ReferenceDate'), type: 'date' },
    { id: 'reference1', label: t('Reference1'), type: 'string' },
    { id: 'reference2', label: t('Reference2'), type: 'string' },
    {
      id: 'description',
      label: t('Details'),
      type: 'string',
      render: ({ row, expanded, onToggle }) => {
        const text = row.description ? t(row.description, { defaultValue: row.description }) : '-';
        if (row._summary) {
          return <SummaryRowLabel row={row} text={text} t={t} expanded={expanded} onToggle={onToggle} />;
        }
        return row._summaryChild ? <SummaryChildLabel text={text} /> : text;
      },
    },
  ];
  

  const formatCell = (value, type, formatDate, t) => {
    if (value == null || value === '') return '-';
    switch (type) {
      case 'number':
        return beautifyNumber(value);
      case 'date':
        return formatDate(value);
      default:
        return value;
    }
  };
  

  const AccountCardDashboardTable = ({
    firstRowDetails,
    filteredAndSortedTransactions,
    handleEdit,
    handleSortChange,
    formatDateNew,
    selectedCurrency = null,
  }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    const showFc = selectedCurrency !== null && selectedCurrency !== undefined;
    const fcOnly = typeof selectedCurrency === 'number';
    const fcLabel = useMemo(() => buildFcLabel(selectedCurrency), [selectedCurrency]);
  
    /* pagination */
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    useEffect(() => setPage(0), [filteredAndSortedTransactions]);
  
    /* columns list */
    const cols = useMemo(() => COLS(t, showFc, fcLabel, fcOnly), [t, showFc, fcLabel, fcOnly]);
  
    /* ---------- 4️⃣  FIRST-ROW + TOTALS LOGIC (unchanged) ---------- */
    const firstRow = useMemo(() => {
      if (fcOnly) {
        const fcBal = firstRowDetails?.foreignCurrencyInitialBalance
          ?? firstRowDetails?.foreignCurrencyBalance
          ?? 0;
        if (fcBal === 0) return null;
        return {
          foreignCurrencyDebit: fcBal > 0 ? fcBal : 0,
          foreignCurrencyCredit: fcBal < 0 ? Math.abs(fcBal) : 0,
          foreignCurrencyBalance: fcBal,
          description: firstRowDetails?.description || 'OpeningBalance',
        };
      }
      if (
        firstRowDetails &&
        typeof firstRowDetails.balance === 'number'
      ) {
        return {
          debitAccountAmount:
            firstRowDetails.balance >= 0 ? firstRowDetails.balance : 0,
          creditAccountAmount:
            firstRowDetails.balance < 0
              ? Math.abs(firstRowDetails.balance)
              : 0,
          balance: firstRowDetails.balance,
          description: firstRowDetails.description,
        };
      }
      return null;
    }, [firstRowDetails, fcOnly]);
  
    const initialDebit = firstRow?.debitAccountAmount || 0;
    const initialCredit = firstRow?.creditAccountAmount || 0;
  
    const totals = useMemo(() => {
      const sum = (key) =>
        filteredAndSortedTransactions.reduce(
          (acc, cur) => acc + (cur[key] || 0),
          0
        );
      return {
        debit: sum('debitAccount') + initialDebit,
        credit: sum('creditAccount') + initialCredit,
      };
    }, [filteredAndSortedTransactions, initialDebit, initialCredit]);
  
    totals.balance = totals.debit - totals.credit;
  
    /* ---------- 5️⃣  PAGINATION ---------- */
    const paginatedRows = useMemo(() => {
      const start = page * rowsPerPage;
      return filteredAndSortedTransactions.slice(
        start,
        start + rowsPerPage
      );
    }, [filteredAndSortedTransactions, page, rowsPerPage]);
  

    
    /* Summary view: merged rows can be opened to show the transactions behind them. */
    const [expandedSummaryRows, setExpandedSummaryRows] = useState({});
    const toggleSummaryRow = useCallback((rowId) => {
      setExpandedSummaryRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
    }, []);

    const displayedRows = useMemo(
      () => paginatedRows.flatMap((row, rowIndex) => {
        const rowKey = row.id ?? `row-${rowIndex}`;
        if (!row._summary || !expandedSummaryRows[rowKey]) return [row];
        const children = (row._children || []).map((child, childIndex) => ({
          ...child,
          id: `${rowKey}:child:${child.id ?? childIndex}`,
          _summaryChild: true,
          _sourceRow: child,
        }));
        return [row, ...children];
      }),
      [paginatedRows, expandedSummaryRows]
    );

    const handleChangePage = useCallback((_, p) => setPage(p), []);
    const handleChangeRowsPerPage = useCallback((e) => {
      setRowsPerPage(parseInt(e.target.value, 10));
      setPage(0);
    }, []);
  
    /* header cell style */
    const headerStyle = {
      backgroundColor: '#E3E7FE',
      color: '#1222b3',
      textAlign: 'center',
      fontFamily: 'Montserrat',
      fontSize: 14,
      fontWeight: 'bold',
      height: 56,
      p: 0,
    };

    const getRowKey = useCallback((row, index) => {
      if (row.id != null) return `id-${row.id}`;
      if (row._id != null) return `oid-${row._id}`;
      return [
        'auto',
        row.documentDate ?? 'na',
        row.reference1 ?? 'na',
        row.reference2 ?? 'na',
        row.debitAccount ?? 0,
        row.creditAccount ?? 0,
        row.balance ?? 0,
        index,
      ].join('-');
    }, []);


    const totalDebit = filteredAndSortedTransactions.reduce(
        (acc, t) => acc + (t.debitAccount || 0),
        initialDebit
    );

    const totalCredit = filteredAndSortedTransactions.reduce(
        (acc, t) => acc + (t.creditAccount || 0),
        initialCredit
    );

    const finalBalance = totalDebit - totalCredit;

    // Foreign-currency opening + totals (single-currency scope).
    const fcInitialBalance = showFc
      ? (firstRowDetails?.foreignCurrencyInitialBalance ?? firstRowDetails?.foreignCurrencyBalance ?? 0)
      : 0;
    const fcTotals = useMemo(() => {
      if (!showFc) return { debit: 0, credit: 0, balance: 0 };
      const debit = filteredAndSortedTransactions.reduce(
        (acc, cur) => acc + (cur.foreignCurrencyDebit || 0),
        fcInitialBalance > 0 ? fcInitialBalance : 0
      );
      const credit = filteredAndSortedTransactions.reduce(
        (acc, cur) => acc + (cur.foreignCurrencyCredit || 0),
        fcInitialBalance < 0 ? Math.abs(fcInitialBalance) : 0
      );
      return { debit, credit, balance: debit - credit };
    }, [showFc, filteredAndSortedTransactions, fcInitialBalance]);

    return (
      <div dir={direction} style={{ width: '100%', height: '55vh' }}>
        <TableContainer
          component={Paper}
          sx={{ height: '50vh', overflow: 'auto', ...TABLE_SCROLLBAR_SX }}
        >
          <Table stickyHeader>
            {/* ---------------------- HEAD ---------------------- */}
            <TableHead>
              <TableRow>
                {cols.map((c) => (
                  <TableCell
                    key={c.id}
                    align="center"
                    sx={{ width: c.width || 'auto', ...headerStyle }}
                    onClick={
                      c.type
                        ? () => handleSortChange(c.id, c.type)
                        : undefined
                    }
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
  
            {/* ---------------------- BODY ---------------------- */}
            <TableBody>
              {/* opening balance row */}
              {firstRow && (
                <TableRow>
                  {cols.map((c) => (
                    <TableCell key={c.id} align="center">
                      {c.id === 'edit'
                        ? null
                        : c.id === 'debitAccount'
                        ? beautifyNumber(firstRow.debitAccountAmount)
                        : c.id === 'creditAccount'
                        ? beautifyNumber(firstRow.creditAccountAmount)
                        : c.id === 'balance'
                        ? beautifyNumber(firstRow.balance)
                        : c.id === 'foreignCurrencyDebit'
                        ? beautifyNumber(firstRow.foreignCurrencyDebit)
                        : c.id === 'foreignCurrencyCredit'
                        ? beautifyNumber(firstRow.foreignCurrencyCredit)
                        : c.id === 'foreignCurrencyBalance'
                        ? beautifyNumber(firstRow.foreignCurrencyBalance ?? fcInitialBalance)
                        : c.id === 'description'
                        ? t(firstRow.description, {
                            defaultValue: firstRow.description,
                          })
                        : '-'}
                    </TableCell>
                  ))}
                </TableRow>
              )}
  
              {/* data rows */}
              {displayedRows.map((row, rowIndex) => (
                <TableRow
                  key={row.id ?? rowIndex}
                  // Rows merged by the Summary view read as one line; tint them so the merge is
                  // visible without reading the chip.
                  sx={row._summary ? {
                    backgroundColor: 'rgba(48, 79, 255, 0.07)',
                    borderInlineStart: '3px solid #304FFF',
                  } : row._summaryChild ? summaryChildRowSx : undefined}
                >
                  {cols.map((c) => (
                    <TableCell key={c.id} align="center">
                      {c.render
                        ? c.render({
                            row,
                            // Sub-items edit the transaction they came from, not the merged row.
                            onEdit: (clicked) => handleEdit(clicked?._sourceRow || clicked),
                            expanded: !!expandedSummaryRows[row.id ?? `row-${rowIndex}`],
                            onToggle: () => toggleSummaryRow(row.id ?? `row-${rowIndex}`),
                          })
                        : c.format
                        ? c.format(row[c.id], t)
                        : formatCell(
                            row[c.id],
                            c.type,
                            formatDateNew,
                            t
                          )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
  
              {/* totals row */}
              <TableRow sx={{ backgroundColor: '#f5f7ff' }}>
                {cols.map((c) => (
                  <TableCell key={c.id} align="center" sx={{ fontWeight: 'bold' }}>
                    {c.id === 'edit'
                      ? null
                      : c.id === 'creditAccountName'
                      ? null
                      : c.id === 'debitAccountName'
                      ? t('Total')
                      : c.id === 'debitAccount'
                      ? `${t('TotalDebitAccount')}: ${beautifyNumber(totals.debit)}`
                      : c.id === 'creditAccount'
                      ? `${t('TotalCreditAccount')}: ${beautifyNumber(totals.credit)}`
                      : c.id === 'balance'
                      ? `${t('Balance')}: ${beautifyNumber(totals.balance)}`
                      : c.id === 'foreignCurrencyDebit'
                      ? beautifyNumber(fcTotals.debit)
                      : c.id === 'foreignCurrencyCredit'
                      ? beautifyNumber(fcTotals.credit)
                      : c.id === 'foreignCurrencyBalance'
                      ? beautifyNumber(fcTotals.balance)
                      : null}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
  
        {/* ------------------- PAGINATION --------------------- */}
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredAndSortedTransactions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('Rows per page')}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} ${t('of')} ${count}`
          }
        />
      </div>
    );
  };
  
  /* no rerender cost for first row */
  export default AccountCardDashboardTable;
  